import { Building, Compass, UserRound, Wrench } from "lucide-mui";
import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useOrganization } from "../../contexts/OrganizationContext";
import { useTenant } from "../../contexts/TenantContext";
import {
  accountNavItems,
  devNavItems,
  exploreNavItems,
  exploreTenantNavItems,
  orgAdminNavItems,
  pathMatches,
  platformNavItems,
  visibleItems,
  type NavSection,
} from "./navItems";

export type NavAreaId = "explore" | "organization" | "platform" | "account";

/** A footer menu of the sidebar and the sibling pages it leads to. */
export interface NavArea {
  id: NavAreaId;
  label: string;
  icon: ReactNode;
  /** Items carry their final link target in `path`; empty sections are dropped. */
  sections: NavSection[];
}

/** Explore, Organization, Platform and Account, with only the links the user may open. */
export function useNavAreas(): NavArea[] {
  const { user } = useAuth();
  const { selectedOrgId, currentOrg } = useOrganization();
  const { selectedTenantId, tenantPath } = useTenant();

  const areas: NavArea[] = [
    {
      id: "explore",
      label: "Explore",
      icon: <Compass />,
      sections: [
        {
          heading: "Tenant",
          items: selectedTenantId
            ? exploreTenantNavItems.map((item) => ({ ...item, path: tenantPath(item.path) }))
            : [],
        },
        { heading: selectedTenantId ? "Global" : undefined, items: exploreNavItems },
      ],
    },
    {
      id: "organization",
      label: "Organization",
      icon: <Building />,
      sections: [{ heading: currentOrg?.name, items: visibleItems(orgAdminNavItems, user, selectedOrgId) }],
    },
    {
      id: "platform",
      label: "Platform",
      icon: <Wrench />,
      sections: [
        { heading: "Platform", items: visibleItems(platformNavItems, user) },
        { heading: "Developer", items: visibleItems(devNavItems, user) },
      ],
    },
    {
      id: "account",
      label: "Account",
      icon: <UserRound />,
      sections: [{ items: accountNavItems }],
    },
  ];
  return areas.map((area) => ({ ...area, sections: area.sections.filter((s) => s.items.length > 0) }));
}

export function areaContains(area: NavArea, pathname: string): boolean {
  return area.sections.some((s) => s.items.some((item) => pathMatches(pathname, item.path)));
}

/** The area the current page belongs to, if any. */
export function useActiveNavArea(areas: NavArea[]): NavArea | null {
  const { pathname } = useLocation();
  return areas.find((area) => areaContains(area, pathname)) ?? null;
}
